import React, { useState, useEffect, useCallback } from "react";
import { NavLink } from "react-router-dom";
import { ChevronLeft, Tag } from "lucide-react";
import showAlert from "../../../server/ShowAlert";
import { validateCoupon, getMyCoupons } from "../../../server/Coupon";
import {
  COUPON_RETURN_CHECKOUT,
  MY_COUPONS_PATH,
  markCouponReturnToCheckout,
} from "../../../utils/couponNavigation";
import { CHECKOUT_STEP } from "../../../constants/checkoutSteps";
import "./CouponCode.scss";

const isExpired = (expiredAt) => {
  if (!expiredAt) return false;
  const t = new Date(expiredAt).getTime();
  return Number.isFinite(t) && t <= Date.now();
};

const belowMinOrderMessage = (minOrder) =>
  `הסכום המינימלי לשימוש בקופון זה הוא ₪${minOrder}`;

const isOrderTotalTooLow = (message) =>
  typeof message === "string" &&
  message.toLowerCase().includes("not valid for this order total");

const couponErrorForCustomer = (message, minOrder = 0) => {
  if (!isOrderTotalTooLow(message)) return message || "קוד הקופון אינו תקין";
  if (minOrder > 0) return belowMinOrderMessage(minOrder);
  return "סכום ההזמנה נמוך מהסכום המינימלי לשימוש בקופון זה";
};

const mapUsableCoupon = (coupon) => {
  const status = coupon.status || "";
  const remaining =
    typeof coupon.sum === "number" && typeof coupon.usedSum === "number"
      ? Math.max(0, Number((coupon.sum - coupon.usedSum).toFixed(2)))
      : Number(coupon.sum) || 0;
  const minOrder = Number(coupon.minOrder) || 0;
  const usable =
    status === "ACTIVE" && !isExpired(coupon.expiredAt) && remaining > 0;

  return {
    id: coupon.id,
    code: coupon.couponCode,
    remaining,
    minOrder,
    usable,
  };
};

const CouponCode = ({
  userId,
  orderSum,
  onCouponApplied,
  checkoutStep = CHECKOUT_STEP.DETAILS,
  stationId,
}) => {
  const [couponCode, setCouponCode] = useState("");
  const [isApplying, setIsApplying] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [myCoupons, setMyCoupons] = useState([]);
  const [loadingMine, setLoadingMine] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!userId && !localStorage.jwtToken) {
        setMyCoupons([]);
        return;
      }
      setLoadingMine(true);
      try {
        if (!stationId) {
          setMyCoupons([]);
          setLoadingMine(false);
          return;
        }
        const res = await getMyCoupons({ branch: String(stationId) });
        if (cancelled) return;
        setMyCoupons(
          (res.data?.coupons ?? [])
            .map(mapUsableCoupon)
            .filter((c) => c.usable && c.code),
        );
      } catch (e) {
        console.error("getMyCoupons in checkout failed", e);
        if (!cancelled) setMyCoupons([]);
      } finally {
        if (!cancelled) setLoadingMine(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [userId, stationId]);

  const applyCode = useCallback(
    async (rawCode) => {
      const code = String(rawCode || "").trim();
      if (!code) {
        showAlert({ msgTitle: "נא להזין קוד קופון", msgType: "error" });
        return;
      }

      const known = myCoupons.find(
        (item) => String(item.code).trim().toLowerCase() === code.toLowerCase(),
      );
      if (known && known.minOrder > 0 && orderSum < known.minOrder) {
        showAlert({
          msgTitle: belowMinOrderMessage(known.minOrder),
          msgType: "error",
        });
        setAppliedCoupon(null);
        onCouponApplied(0, null);
        return;
      }

      setIsApplying(true);
      try {
        const response = await validateCoupon(
          code,
          userId,
          orderSum,
          stationId,
        );
        if (response.Success) {
          const minOrder = Number(response.Coupon?.MinOrder) || 0;
          if (minOrder > 0 && orderSum < minOrder) {
            showAlert({
              msgTitle: belowMinOrderMessage(minOrder),
              msgType: "error",
            });
            setAppliedCoupon(null);
            onCouponApplied(0, null);
            return;
          }

          const acceptedCode = response.Coupon?.CouponCode || code;
          const discount = Number(response.Coupon.Sum) || 0;
          setAppliedCoupon({ code: acceptedCode, discount });
          setCouponCode(acceptedCode);
          onCouponApplied(discount, acceptedCode);
          showAlert({ msgTitle: "הקופון הוחל בהצלחה!", msgType: "success" });
        } else {
          const errorMsg = couponErrorForCustomer(
            response.Message,
            known?.minOrder || 0,
          );
          showAlert({ msgTitle: errorMsg, msgType: "error" });
          setAppliedCoupon(null);
          onCouponApplied(0, null);
        }
      } catch (error) {
        const errorMsg = couponErrorForCustomer(
          error?.Message ||
            error?.response?.data?.error ||
            error?.response?.data?.message,
          known?.minOrder || 0,
        );
        showAlert({ msgTitle: errorMsg, msgType: "error" });
        setAppliedCoupon(null);
        onCouponApplied(0, null);
      } finally {
        setIsApplying(false);
      }
    },
    [userId, orderSum, stationId, onCouponApplied, myCoupons],
  );

  const handleApplyCoupon = () => {
    applyCode(couponCode);
  };

  const handleSelectMine = (coupon) => {
    if (isApplying || appliedCoupon) return;
    if (coupon.minOrder > 0 && orderSum < coupon.minOrder) {
      showAlert({
        msgTitle: belowMinOrderMessage(coupon.minOrder),
        msgType: "error",
      });
      return;
    }
    applyCode(coupon.code);
  };

  const handleRemoveCoupon = () => {
    setCouponCode("");
    setAppliedCoupon(null);
    onCouponApplied(0, null);
  };

  const selectableMine = myCoupons.filter((c) => {
    if (appliedCoupon?.code) {
      return (
        String(c.code).toLowerCase() !==
        String(appliedCoupon.code).toLowerCase()
      );
    }
    return true;
  });

  return (
    <section className="coupon-code-section">
      {!appliedCoupon && (loadingMine || selectableMine.length > 0) ? (
        <div className="coupon-mine-block">
          <div className="coupon-mine-head">
            <span className="coupon-mine-title">הקופונים הפעילים שלך</span>
            <NavLink
              to={MY_COUPONS_PATH}
              state={{ couponReturnTo: COUPON_RETURN_CHECKOUT }}
              onClick={() => markCouponReturnToCheckout(checkoutStep)}
              className="coupon-my-coupons-link coupon-my-coupons-link--inline"
            >
              לכל הקופונים
              <ChevronLeft size={14} strokeWidth={2.4} aria-hidden />
            </NavLink>
          </div>
          {loadingMine ? (
            <p className="coupon-mine-loading">טוען קופונים...</p>
          ) : (
            <div className="coupon-mine-list" role="list">
              {selectableMine.map((coupon) => {
                const blockedByMin =
                  coupon.minOrder > 0 && orderSum < coupon.minOrder;
                return (
                  <button
                    key={coupon.id}
                    type="button"
                    role="listitem"
                    className={`coupon-mine-chip${blockedByMin ? " is-blocked" : ""}`}
                    onClick={() => handleSelectMine(coupon)}
                    disabled={isApplying || blockedByMin}
                    title={
                      blockedByMin
                        ? `מינימום הזמנה ₪${coupon.minOrder}`
                        : `החל קופון ${coupon.code}`
                    }
                  >
                    <Tag size={14} strokeWidth={2} aria-hidden />
                    <span className="coupon-mine-chip__sum">
                      ₪{coupon.remaining}
                    </span>
                    <span className="coupon-mine-chip__code">
                      {coupon.code}
                    </span>
                    {blockedByMin ? (
                      <span className="coupon-mine-chip__hint">
                        מ־₪{coupon.minOrder}
                      </span>
                    ) : (
                      <span className="coupon-mine-chip__action">החל</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ) : null}

      <div className="coupon-box-with-link">
        <div className="coupon-window">
          <div className="coupon-form">
            {!appliedCoupon ? (
              <div className="coupon-input-area">
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleApplyCoupon();
                    }
                  }}
                  placeholder="או הזן קוד קופון ידנית"
                  className="coupon-text-input"
                  disabled={isApplying}
                  aria-label="קוד קופון"
                />
                <button
                  type="button"
                  onClick={handleApplyCoupon}
                  disabled={isApplying || !couponCode.trim()}
                  className="btn-apply-coupon"
                >
                  {isApplying ? "בודק..." : "החל קופון"}
                </button>
              </div>
            ) : (
              <div className="coupon-applied-area">
                <div className="applied-info">
                  <span className="applied-code">
                    קופון: {appliedCoupon.code}
                  </span>
                  <span className="applied-discount">
                    הנחה: ₪{appliedCoupon.discount.toFixed(2)}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveCoupon}
                  className="btn-remove-coupon"
                  disabled={isApplying}
                >
                  הסר קופון
                </button>
              </div>
            )}
          </div>
        </div>
        {!loadingMine && selectableMine.length === 0 ? (
          <NavLink
            to={MY_COUPONS_PATH}
            state={{ couponReturnTo: COUPON_RETURN_CHECKOUT }}
            onClick={() => markCouponReturnToCheckout(checkoutStep)}
            className="coupon-my-coupons-link"
          >
            הקופונים שלי
            <ChevronLeft size={14} strokeWidth={2.4} aria-hidden />
          </NavLink>
        ) : null}
      </div>
    </section>
  );
};

export default CouponCode;
